// schemaSlice — Redux slice for the Schema Surface.
// Schema maps are indexed by composite key: `${tenantId}::${schemaMapId}`.

import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type {
  SchemaMap,
  SubgraphRegistration,
  SchemaElement,
  SchemaEvolution,
  DomainToSchemaMapping,
  SchemaElementStatus,
} from "../../types/index.ts";
import type { RootState } from "../index.ts";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function schemaKey(tenantId: string, schemaMapId: string): string {
  return `${tenantId}::${schemaMapId}`;
}

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

export interface SchemaState {
  schemaMaps: Record<string, SchemaMap>;
}

const initialState: SchemaState = {
  schemaMaps: {},
};

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const schemaSlice = createSlice({
  name: "schema",
  initialState,
  reducers: {
    initializeSchemaMap(
      state,
      action: PayloadAction<{ tenantId: string; schemaMapId: string; name: string }>,
    ) {
      const { tenantId, schemaMapId, name } = action.payload;
      const key = schemaKey(tenantId, schemaMapId);
      if (!state.schemaMaps[key]) {
        state.schemaMaps[key] = {
          tenantId,
          schemaMapId,
          name,
          subgraphs: {},
          elements: {},
          mappings: [],
          evolutions: [],
          version: 1,
          updatedAt: new Date().toISOString(),
        };
      }
    },

    registerSubgraph(
      state,
      action: PayloadAction<{
        tenantId: string;
        schemaMapId: string;
        subgraph: SubgraphRegistration;
      }>,
    ) {
      const { tenantId, schemaMapId, subgraph } = action.payload;
      const key = schemaKey(tenantId, schemaMapId);
      const map = state.schemaMaps[key];
      if (!map) return;
      map.subgraphs[subgraph.subgraphId] = subgraph;
      map.version++;
      map.updatedAt = new Date().toISOString();
    },

    addSchemaElement(
      state,
      action: PayloadAction<{
        tenantId: string;
        schemaMapId: string;
        element: SchemaElement;
      }>,
    ) {
      const { tenantId, schemaMapId, element } = action.payload;
      const key = schemaKey(tenantId, schemaMapId);
      const map = state.schemaMaps[key];
      if (!map) return;
      map.elements[element.elementId] = element;

      // Add elementId to subgraph's elementIds
      const subgraph = map.subgraphs[element.subgraphId];
      if (subgraph && !subgraph.elementIds.includes(element.elementId)) {
        subgraph.elementIds.push(element.elementId);
      }

      map.version++;
      map.updatedAt = new Date().toISOString();
    },

    deprecateElement(
      state,
      action: PayloadAction<{
        tenantId: string;
        schemaMapId: string;
        elementId: string;
        reason: string;
        since: string;
        removalTarget?: string;
      }>,
    ) {
      const { tenantId, schemaMapId, elementId, reason, since, removalTarget } = action.payload;
      const key = schemaKey(tenantId, schemaMapId);
      const map = state.schemaMaps[key];
      if (!map) return;

      const element = map.elements[elementId];
      if (!element) return;

      element.status = "deprecated";
      element.deprecatedSince = since;
      element.deprecationReason = reason;
      if (removalTarget) element.removalTarget = removalTarget;

      map.version++;
      map.updatedAt = new Date().toISOString();
    },

    removeElement(
      state,
      action: PayloadAction<{
        tenantId: string;
        schemaMapId: string;
        elementId: string;
      }>,
    ) {
      const { tenantId, schemaMapId, elementId } = action.payload;
      const key = schemaKey(tenantId, schemaMapId);
      const map = state.schemaMaps[key];
      if (!map) return;

      const element = map.elements[elementId];
      if (element) {
        element.status = "removed";
      }

      map.version++;
      map.updatedAt = new Date().toISOString();
    },

    flagBreakingChange(
      state,
      action: PayloadAction<{
        tenantId: string;
        schemaMapId: string;
        elementId: string;
        consumerImpact: string[];
      }>,
    ) {
      const { tenantId, schemaMapId, elementId, consumerImpact } = action.payload;
      const key = schemaKey(tenantId, schemaMapId);
      const map = state.schemaMaps[key];
      if (!map) return;

      const element = map.elements[elementId];
      if (!element) return;

      element.isBreakingChange = true;
      element.consumerImpact = consumerImpact;

      map.version++;
      map.updatedAt = new Date().toISOString();
    },

    addDomainMapping(
      state,
      action: PayloadAction<{
        tenantId: string;
        schemaMapId: string;
        mapping: DomainToSchemaMapping;
      }>,
    ) {
      const { tenantId, schemaMapId, mapping } = action.payload;
      const key = schemaKey(tenantId, schemaMapId);
      const map = state.schemaMaps[key];
      if (!map) return;

      // Replace existing mapping for this element, or add new
      const idx = map.mappings.findIndex((m) => m.schemaElementId === mapping.schemaElementId);
      if (idx >= 0) {
        map.mappings[idx] = mapping;
      } else {
        map.mappings.push(mapping);
      }

      // Also attach to element
      const element = map.elements[mapping.schemaElementId];
      if (element) {
        element.domainMapping = mapping;
      }

      map.version++;
      map.updatedAt = new Date().toISOString();
    },

    recordEvolution(
      state,
      action: PayloadAction<{
        tenantId: string;
        schemaMapId: string;
        evolution: SchemaEvolution;
      }>,
    ) {
      const { tenantId, schemaMapId, evolution } = action.payload;
      const key = schemaKey(tenantId, schemaMapId);
      const map = state.schemaMaps[key];
      if (!map) return;
      map.evolutions.push(evolution);
    },
  },
});

export const {
  initializeSchemaMap,
  registerSubgraph,
  addSchemaElement,
  deprecateElement,
  removeElement,
  flagBreakingChange,
  addDomainMapping,
  recordEvolution,
} = schemaSlice.actions;

export default schemaSlice.reducer;

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

export const selectAllSchemaMaps = (state: RootState): SchemaMap[] =>
  Object.values(state.schema.schemaMaps);

export const selectSchemaMapByKey = (
  state: RootState,
  tenantId: string,
  schemaMapId: string,
): SchemaMap | undefined =>
  state.schema.schemaMaps[schemaKey(tenantId, schemaMapId)];

export const selectSubgraphs = (
  state: RootState,
  tenantId: string,
  schemaMapId: string,
): SubgraphRegistration[] => {
  const map = state.schema.schemaMaps[schemaKey(tenantId, schemaMapId)];
  return map ? Object.values(map.subgraphs) : [];
};

export const selectSubgraphByContext = (
  state: RootState,
  tenantId: string,
  schemaMapId: string,
  contextId: string,
): SubgraphRegistration | undefined => {
  const map = state.schema.schemaMaps[schemaKey(tenantId, schemaMapId)];
  if (!map) return undefined;
  return Object.values(map.subgraphs).find((sg) => sg.contextId === contextId);
};

export const selectAllElements = (
  state: RootState,
  tenantId: string,
  schemaMapId: string,
): SchemaElement[] => {
  const map = state.schema.schemaMaps[schemaKey(tenantId, schemaMapId)];
  return map ? Object.values(map.elements) : [];
};

export const selectElementById = (
  state: RootState,
  tenantId: string,
  schemaMapId: string,
  elementId: string,
): SchemaElement | undefined => {
  const map = state.schema.schemaMaps[schemaKey(tenantId, schemaMapId)];
  return map?.elements[elementId];
};

export const selectElementsBySubgraph = (
  state: RootState,
  tenantId: string,
  schemaMapId: string,
  subgraphId: string,
): SchemaElement[] => {
  const map = state.schema.schemaMaps[schemaKey(tenantId, schemaMapId)];
  if (!map) return [];
  return Object.values(map.elements).filter((e) => e.subgraphId === subgraphId);
};

export const selectElementsByKind = (
  state: RootState,
  tenantId: string,
  schemaMapId: string,
  kind: string,
): SchemaElement[] => {
  const map = state.schema.schemaMaps[schemaKey(tenantId, schemaMapId)];
  if (!map) return [];
  return Object.values(map.elements).filter((e) => e.kind === kind);
};

export const selectElementsByStatus = (
  state: RootState,
  tenantId: string,
  schemaMapId: string,
  status: SchemaElementStatus,
): SchemaElement[] => {
  const map = state.schema.schemaMaps[schemaKey(tenantId, schemaMapId)];
  if (!map) return [];
  return Object.values(map.elements).filter((e) => e.status === status);
};

export const selectDeprecatedElements = (
  state: RootState,
  tenantId: string,
  schemaMapId: string,
): SchemaElement[] =>
  selectElementsByStatus(state, tenantId, schemaMapId, "deprecated");

export const selectBreakingChanges = (
  state: RootState,
  tenantId: string,
  schemaMapId: string,
): SchemaElement[] => {
  const map = state.schema.schemaMaps[schemaKey(tenantId, schemaMapId)];
  if (!map) return [];
  return Object.values(map.elements).filter((e) => e.isBreakingChange);
};

export const selectMappingsByContext = (
  state: RootState,
  tenantId: string,
  schemaMapId: string,
  contextId: string,
): DomainToSchemaMapping[] => {
  const map = state.schema.schemaMaps[schemaKey(tenantId, schemaMapId)];
  if (!map) return [];
  return map.mappings.filter((m) => m.contextId === contextId);
};

export const selectMappingForElement = (
  state: RootState,
  tenantId: string,
  schemaMapId: string,
  elementId: string,
): DomainToSchemaMapping | undefined => {
  const map = state.schema.schemaMaps[schemaKey(tenantId, schemaMapId)];
  if (!map) return undefined;
  return map.mappings.find((m) => m.schemaElementId === elementId);
};

export const selectMappingsByDomainConcept = (
  state: RootState,
  tenantId: string,
  schemaMapId: string,
  domainConceptId: string,
): DomainToSchemaMapping[] => {
  const map = state.schema.schemaMaps[schemaKey(tenantId, schemaMapId)];
  if (!map) return [];
  return map.mappings.filter((m) => m.domainConceptId === domainConceptId);
};

export const selectEvolutions = (
  state: RootState,
  tenantId: string,
  schemaMapId: string,
): SchemaEvolution[] => {
  const map = state.schema.schemaMaps[schemaKey(tenantId, schemaMapId)];
  return map?.evolutions ?? [];
};

export const selectSchemaMapCount = (state: RootState): number =>
  Object.keys(state.schema.schemaMaps).length;
